import React from 'react';
import { Loader2, FileText, Image, Music, Film, File, Download, ExternalLink } from 'lucide-react';

interface DocumentPreviewProps {
  documentUrl?: string;
  fileName: string;
  isLoading?: boolean;
  isUser?: boolean;
}

const getFileTypeInfo = (fileName: string) => {
  const ext = fileName.split('.').pop()?.toLowerCase() || '';

  if (['pdf'].includes(ext)) return { type: 'PDF', icon: FileText, bg: 'bg-danger-subtle', text: 'text-danger-text' };
  if (['doc', 'docx'].includes(ext)) return { type: 'Word', icon: FileText, bg: 'bg-info-subtle', text: 'text-info-text' };
  if (['xls', 'xlsx'].includes(ext)) return { type: 'Excel', icon: FileText, bg: 'bg-success-subtle', text: 'text-success-text' };
  if (['ppt', 'pptx'].includes(ext)) return { type: 'Slides', icon: FileText, bg: 'bg-warning-subtle', text: 'text-warning-text' };
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext)) return { type: 'Imagem', icon: Image, bg: 'bg-accent', text: 'text-accent-foreground' };
  if (['mp3', 'wav', 'ogg', 'aac', 'flac', 'webm'].includes(ext)) return { type: 'Áudio', icon: Music, bg: 'bg-warning-subtle', text: 'text-warning-text' };
  if (['mp4', 'avi', 'mov', 'mkv', 'webm'].includes(ext)) return { type: 'Vídeo', icon: Film, bg: 'bg-secondary', text: 'text-accent-foreground' };
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return { type: 'Compactado', icon: File, bg: 'bg-warning-subtle', text: 'text-warning-text' };

  return { type: ext.toUpperCase() || 'Arquivo', icon: File, bg: 'bg-muted', text: 'text-muted-foreground' };
};

const formatFileSize = (url?: string): string | null => {
  return null;
};

export const DocumentPreview: React.FC<DocumentPreviewProps> = ({ documentUrl, fileName, isLoading = false, isUser = false }) => {
  const handleOpen = () => {
    if (documentUrl) window.open(documentUrl, '_blank');
  };

  const handleDownload = () => {
    if (!documentUrl) return;
    const a = document.createElement('a');
    a.href = documentUrl;
    a.download = fileName;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const { type, icon: TypeIcon, bg, text } = getFileTypeInfo(fileName);

  if (isLoading) {
    return (
      <div className={`p-3 rounded-xl flex items-center gap-3 max-w-[280px] ${isUser ? 'bg-accent/50 border border-primary/40' : 'bg-muted border border-border/60'}`}>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isUser ? 'bg-accent/50' : 'bg-secondary'}`}>
          <Loader2 className={`w-5 h-5 animate-spin ${isUser ? 'text-accent-foreground' : 'text-muted-foreground'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium truncate ${isUser ? 'text-accent-foreground' : 'text-foreground'}`}>{fileName}</p>
          <p className={`text-xs mt-0.5 ${isUser ? 'text-accent-foreground' : 'text-muted-foreground'}`}>Enviando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`group p-3 rounded-xl transition-all duration-150 max-w-[300px] ${
      isUser
        ? 'bg-accent/40 border border-primary/30 hover:bg-accent/60'
        : 'bg-muted/80 border border-border/60 hover:bg-muted/80'
    }`}>
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${bg}`}>
          <TypeIcon className={`w-5 h-5 ${text}`} />
        </div>

        <div className="flex-1 min-w-0">
          <p className={`text-sm font-semibold truncate leading-tight ${isUser ? 'text-foreground' : 'text-foreground'}`}>
            {fileName}
          </p>
          <p className={`text-xs mt-1 ${isUser ? 'text-accent-foreground' : 'text-muted-foreground'}`}>
            {type}
          </p>
        </div>
      </div>

      {documentUrl && (
        <div className="flex items-center gap-2 mt-3 pt-2.5 border-t border-border/40">
          <button
            onClick={handleOpen}
            className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 px-3 rounded-lg text-xs font-semibold transition-all duration-150 cursor-pointer ${
              isUser
                ? 'bg-accent/60 text-accent-foreground hover:bg-accent active:scale-[0.97]'
                : 'bg-secondary text-foreground/80 hover:bg-secondary active:scale-[0.97]'
            }`}
            type="button"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Abrir
          </button>

          <button
            onClick={handleDownload}
            className={`flex items-center justify-center p-1.5 rounded-lg transition-all duration-150 cursor-pointer ${
              isUser
                ? 'text-accent-foreground hover:bg-accent/60 active:scale-[0.95]'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground active:scale-[0.95]'
            }`}
            type="button"
            title="Baixar arquivo"
          >
            <Download className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default DocumentPreview;
